import {
  Phone,
  Mail,
  MapPin,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Twitter,
  MessageCircle,
} from "lucide-react";
import { BRAND, NAV_LINKS, SOCIAL_LINKS } from "@/lib/siteData";

const ICON_MAP = {
  instagram: Instagram,
  facebook: Facebook,
  linkedin: Linkedin,
  youtube: Youtube,
  twitter: Twitter,
  whatsapp: MessageCircle,
};

export const Footer = () => {
  const activeSocials = SOCIAL_LINKS.filter((s) => s.url && s.url.trim());
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-[#E3A21A]/10 bg-[#0d140d] pt-20 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <div className="bg-white rounded-md p-1.5 flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_revenue-systems-14/artifacts/4p9j5b60_Untitled%20design.jpg"
                  alt="Digibis Digital by Sam Consolidates"
                  className="h-11 w-11 object-contain"
                />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-display text-xl tracking-tight text-[#F5F5F0]">
                  Digibis<span className="text-[#E3A21A]">.</span>
                  <span className="text-[#A8B8A8] font-normal ml-1">Digital</span>
                </span>
                <span className="text-[10px] tracking-[0.25em] uppercase text-[#A8B8A8]/70 font-semibold">
                  by Sam Consolidates
                </span>
              </div>
            </div>
            <p className="mt-6 text-[#A8B8A8] text-base leading-relaxed max-w-sm">
              {BRAND.tagline} Performance marketing partners for real estate,
              lifestyle and education brands across India.
            </p>
            <div className="mt-8 space-y-3 text-sm text-[#A8B8A8]">
              <a
                href={BRAND.phoneHref}
                data-testid="footer-phone"
                className="flex items-center gap-3 hover:text-[#E3A21A] transition-colors"
              >
                <Phone className="h-4 w-4 text-[#E3A21A]" />
                {BRAND.phone}
              </a>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="footer-email"
                className="flex items-center gap-3 hover:text-[#E3A21A] transition-colors"
              >
                <Mail className="h-4 w-4 text-[#E3A21A]" />
                {BRAND.email}
              </a>
              <div className="flex items-center gap-3">
                <MapPin className="h-4 w-4 text-[#E3A21A]" />
                {BRAND.location}
              </div>
            </div>

            {/* Social Icons */}
            {activeSocials.length > 0 && (
              <div className="mt-8" data-testid="footer-socials">
                <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E3A21A] mb-4">
                  Follow Us
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {activeSocials.map((s) => {
                    const Icon = ICON_MAP[s.icon] || MessageCircle;
                    return (
                      <a
                        key={s.name}
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.name}
                        data-testid={`social-${s.icon}`}
                        className="group h-11 w-11 rounded-full border border-[#E3A21A]/20 bg-[#1E2A1E] text-[#A8B8A8] hover:bg-[#E3A21A] hover:text-[#121A12] hover:border-[#E3A21A] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center"
                      >
                        <Icon className="h-4 w-4" />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="md:col-span-3">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E3A21A] mb-5">
              Sections
            </div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    data-testid={`footer-link-${l.href.replace("#", "")}`}
                    className="text-sm text-[#A8B8A8] hover:text-[#E3A21A] transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E3A21A] mb-5">
              Niches We Scale
            </div>
            <ul className="space-y-3">
              <li className="text-sm text-[#A8B8A8]">
                Real Estate Developers, Realtors, Builders
              </li>
              <li className="text-sm text-[#A8B8A8]">
                Lifestyle, Luxury & Hospitality Brands
              </li>
              <li className="text-sm text-[#A8B8A8]">
                Coaching Institutes, Course Creators, EdTech
              </li>
            </ul>

            <div className="mt-10 text-[10px] tracking-[0.25em] uppercase font-bold text-[#E3A21A] mb-4">
              Services
            </div>
            <p className="text-sm text-[#A8B8A8] leading-relaxed">
              Performance Marketing · Funnels · Content Creation · Social Growth
              · SEO
            </p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#E3A21A]/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[#A8B8A8]">
            © {new Date().getFullYear()} Digibis Digital. All rights reserved.
          </div>
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#E3A21A]">
            Built for founders who want receipts, not promises.
          </div>
        </div>
      </div>
    </footer>
  );
};
